import { useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import type { FlattenSimpleInterpolation } from 'styled-components';
import { FlexBox } from './FlexBox';
import TextCheckbox from './TextCheckbox';
import {
  COLOR_BLACK,
  COLOR_BLUE_8,
  COLOR_LIGHT_GRAY,
  COLOR_SUCCESS_LIGHT
} from 'shared/helpers/colors';
import { Paragraph } from './Paragraph';
import { ClickAwayListener } from '@mui/material';
import useSales from 'shared/hooks/useSales';

const cssCheckboxText = css`
  font-family: Plus Jakarta Sans;
  font-size: 15px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0px;
  text-align: left;

  @media only screen and (max-width: 70em) {
    font-size: 14px;
  }

  @media only screen and (max-width: 50em) {
    font-size: 12px;
  }
`;

const CheckBtn = styled.button<{ hasUrl?: boolean }>`
  height: 48px;
  width: 15%;
  border-radius: 0px 10px 10px 0px !important;
  border: 1px solid ${COLOR_LIGHT_GRAY};
  background: ${(props) => (props.hasUrl ? COLOR_SUCCESS_LIGHT : COLOR_BLUE_8)};
  position: absolute;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.3s ease;

  img {
    width: 17px;
    height: 12px;
  }

  &:hover {
    background: ${(props) => (props.hasUrl ? `#74b177` : `#199094`)};
    transition: background 0.3s ease;
    cursor: pointer;
  }
`;

const fadeInTwo = keyframes`
  from {
    opacity: 0;
    transform: translateY(-30px) scale(0.75);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const LinkInput = styled.input`
  width: 100%;
  height: 48px;
  border-radius: 10px;
  border: 1px solid ${COLOR_LIGHT_GRAY};
  border-right: none;
  padding: 10px 15% 10px 14px;
  filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.12));
  transition: filter 0.3s ease;
  outline: none;
`;

const PopUpLink = styled.div<{ css?: FlattenSimpleInterpolation }>`
  width: 525px;
  z-index: 55;
  left: -36px;
  background: #fff8e3;
  border-radius: 7px;
  border: 1px solid #acacac;
  cursor: default;
  animation: ${fadeInTwo} 0.2s ease-in-out;
  color: ${COLOR_BLACK};

  @media screen and (max-width: 991px) {
    width: 475px;
    left: -10px;
  }

  @media screen and (max-width: 768px) {
    width: 375px;
  }

  @media screen and (max-width: 465px) {
    width: 300px;
  }

  @media screen and (max-width: 340px) {
    width: 270px;
    left: -99px;
  }

  ${(props) => props.css || ''}
`;

const ClosePopUpLink = styled.img`
  position: absolute;
  right: 12px;
  top: 12px;
  width: 9px;
  height: 9px;
  cursor: pointer;
`;

const InnerRelativeLink = styled.div`
  position: relative;
  box-shadow: rgba(0, 0, 0, 0.15) 0px 4px 7px 0px;
  border-radius: 7px;
`;

const SvgArrowBot = styled.svg`
  position: absolute;
  bottom: -8px;
  width: 20px;
  height: 9px;
  fill: none;
  left: 0px;
  right: 0px;
  margin: 0 auto;
  z-index: 33;
  transform: rotate(180deg);
`;

const PopUpInner = styled.div`
  padding: 28px 34px 38px;
  text-align: left;

  @media screen and (max-width: 991px) {
    padding: 20px 24px 28px;
  }
`;

const LinkTitle = styled.h3`
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  margin-bottom: 10px;
  font-weight: 700;
  color: ${COLOR_BLACK};
`;

const Form = styled.div`
  display: flex;
  width: 100%;
  position: relative;

  &:hover input {
    filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0));
    transition: filter 0.3s ease;
  }

  & button {
    border-radius: 6px !important;
  }
`;

interface ButtonActionModalProps {
  onClose: () => void;
  meta: Record<string, unknown>;
  cssContainer?: FlattenSimpleInterpolation;
}

const ButtonActionModal = ({ onClose, meta, cssContainer }: ButtonActionModalProps) => {
  const { updateButtonUrl } = useSales();

  const linkRef = useRef<HTMLInputElement | null>(null);

  const [isNextStepChecked, setIsNextStepChecked] = useState(!meta?.['url']);
  const [link, setLink] = useState((meta?.['url'] as string) || '');
  const [isDisabled, setIsDisabled] = useState(false);

  const handleSubmit = () => {
    if (link) {
      updateButtonUrl(meta['accessorKey'] as string, link);
      setIsNextStepChecked(false);
      setIsDisabled(false);
    }
  };

  const handleNextStepCheckbox = () => {
    setIsNextStepChecked(!isNextStepChecked);
    updateButtonUrl(meta['accessorKey'] as string, link, true);
    setIsDisabled(!isNextStepChecked);
  };

  return (
    <ClickAwayListener onClickAway={() => onClose()}>
      <PopUpLink css={cssContainer}>
        <InnerRelativeLink>
          <ClosePopUpLink onClick={onClose} src="/img/close_link.svg" alt="X" />
          <PopUpInner>
            <FlexBox flexDirection="column" mb="20px" gap={5}>
              <h2>Button Action:</h2>
              <TextCheckbox
                id="not-editable-pdf"
                label={
                  <Paragraph fontSize="25px" onClick={handleNextStepCheckbox}>
                    Link to the next step
                  </Paragraph>
                }
                checked={isNextStepChecked}
                onCheckboxClick={() => setIsNextStepChecked?.(!isNextStepChecked)}
                cssLabel={cssCheckboxText}
                cssContainer={css`
                  display: flex;
                  align-items: center;
                `}
                cssCheckbox={css`
                  margin-top: 2.5px;
                `}
              />
            </FlexBox>
            <LinkTitle>Change the URL destination:</LinkTitle>
            <Form onClick={handleSubmit}>
              <LinkInput
                ref={linkRef}
                disabled={isDisabled}
                placeholder="  "
                required
                value={link}
                style={{
                  fontSize: '20px'
                }}
                onChange={(e) => setLink(e.target.value)}
                onClick={(e) => {
                  e.preventDefault();
                  e.currentTarget.focus();
                }}
              />
              <CheckBtn type="submit" hasUrl={!!meta?.['url']}>
                <img src={'/img/color_check.svg'} alt="✓" />
              </CheckBtn>
            </Form>
          </PopUpInner>
          <SvgArrowBot className="svgBot" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 21 11">
            <path d="M11 1L20.5 10.5H0.5L11 1Z" fill="#FFF8E3" />
            <path d="M20 9.5L11 1L1 9.5" stroke="#ACACAC" strokeLinecap="round" />
          </SvgArrowBot>
        </InnerRelativeLink>
      </PopUpLink>
    </ClickAwayListener>
  );
};

export default ButtonActionModal;
